import React, { useState, useMemo } from 'react';
import { useStore } from '../store/StoreContext';
import { calculateSchedule, getStatusColor, normalizeStatus, toLocalISO } from '../utils/helpers';
import SafeIcon from '../common/SafeIcon';
import { Button } from '../components/UI/Button';
import { Select } from '../components/UI/Input';
import { motion, AnimatePresence } from 'framer-motion';
import { ItemEditorModal } from '../components/UI/ItemEditorModal';

const CalendarTaskItem = ({ item, isIlluminated, onHover, onClick }) => {
  const projectColor = item.color || '#6C63FF';
  const statusColor = getStatusColor(item.status);

  return (
    <motion.div 
      onMouseEnter={() => onHover(item.projectId)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      animate={{ 
        scale: isIlluminated ? 1.05 : 1,
        borderColor: isIlluminated ? projectColor : 'transparent',
        backgroundColor: isIlluminated ? 'rgba(255,255,255,0.05)' : 'rgba(26,29,39,1)'
      }}
      className={`group/item text-[9px] px-2 py-1.5 rounded truncate flex items-center bg-base-800 border transition-all cursor-pointer ${
        isIlluminated ? 'z-10 relative shadow-lg' : 'border-base-700'
      }`}
    >
      <div className="w-2 h-2 rounded-full mr-2 shrink-0 shadow-sm" style={{ backgroundColor: statusColor }} />
      <span className={`truncate ${isIlluminated ? 'font-bold text-white' : 'text-gray-400 group-hover/item:text-gray-200'}`}>
        {item.name}
      </span>
    </motion.div>
  );
};

export const CalendarView = () => {
  const { state } = useStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedBU, setSelectedBU] = useState('all');
  const [hoveredProjectId, setHoveredProjectId] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const filteredProjects = useMemo(() => {
    const projects = Object.values(state.projects || {});
    return projects
      .filter(p => selectedBU === 'all' || p.businessUnitId === selectedBU)
      .map(p => calculateSchedule(p));
  }, [state.projects, selectedBU]);

  const getItemsForDay = (day) => {
    if (!day) return [];
    // CRITICAL: Use toLocalISO instead of toISOString to prevent date shift
    // Fixed variable name to targetStr
    const targetStr = toLocalISO(new Date(year, month, day));
    
    const items = [];
    filteredProjects.forEach(p => {
      p.scheduledWorkflows?.forEach(wf => {
        wf.scheduledTasks?.forEach(task => {
          if (targetStr >= task.startDate && targetStr <= task.endDate) {
            items.push({
              ...task,
              projectName: p.name,
              projectId: p.id,
              color: p.color || '#6C63FF',
              type: 'task'
            });
          }
        });
      });
    });
    return items;
  };

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col bg-base-900">
      <div className="p-6 border-b border-base-700 bg-base-800 flex flex-wrap justify-between items-center gap-4 shrink-0">
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-2xl font-bold">Calendar</h1>
            <p className="text-sm text-gray-400">Project timeline by month.</p>
          </div>
          <div className="w-64">
            <Select 
              label="Filter by Business Unit" 
              value={selectedBU} 
              onChange={(e) => setSelectedBU(e.target.value)}
              options={[{ label: 'All Business Units', value: 'all' }, ...Object.values(state.businessUnits || {}).map(bu => ({ label: bu.name, value: bu.id }))]}
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>
            <SafeIcon name="ChevronLeft" />
          </Button>
          <span className="text-xl font-mono font-semibold w-56 text-center text-white">
            {monthNames[month]} {year}
          </span>
          <Button variant="secondary" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>
            <SafeIcon name="ChevronRight" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 p-6 overflow-auto custom-scrollbar">
          <div className="grid grid-cols-7 gap-px bg-base-700 rounded-xl overflow-hidden border border-base-700 shadow-2xl">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="bg-base-800 p-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">{d}</div>
            ))}
            {days.map((day, index) => {
              if (day === null) return <div key={`empty-${index}`} className="bg-base-900/30 min-h-[140px]"></div>;
              
              const dayItems = getItemsForDay(day);
              const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
              
              return (
                <div 
                  key={day} 
                  onClick={() => setSelectedDay(day)}
                  className={`bg-base-900 min-h-[140px] p-2 hover:bg-base-800/50 transition-colors cursor-pointer relative group ${selectedDay === day ? 'bg-base-800' : ''}`}
                >
                  <div className={`text-xs font-bold w-7 h-7 flex items-center justify-center rounded-lg mb-2 transition-colors ${
                    isToday ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-gray-500 group-hover:text-gray-300'
                  }`}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {dayItems.slice(0, 4).map((item, i) => (
                      <CalendarTaskItem 
                        key={`${item.id}-${i}`}
                        item={item}
                        isIlluminated={hoveredProjectId === item.projectId}
                        onHover={setHoveredProjectId}
                        onClick={() => setEditingItem({ item, projectId: item.projectId, type: 'task' })}
                      />
                    ))}
                    {dayItems.length > 4 && (
                      <div className="text-[9px] text-gray-500 font-bold pl-1 pt-1">+ {dayItems.length - 4} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <AnimatePresence>
          {selectedDay && (
            <motion.div 
              initial={{ x: 320 }}
              animate={{ x: 0 }}
              exit={{ x: 320 }}
              className="w-80 bg-base-800 border-l border-base-700 flex flex-col shadow-2xl"
            >
              <div className="p-4 border-b border-base-700 flex justify-between items-center bg-base-900/50">
                <h3 className="font-bold text-sm text-white">{monthNames[month]} {selectedDay}, {year}</h3>
                <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-base-700 rounded-md text-gray-400 hover:text-white transition-colors">
                  <SafeIcon name="X" className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 flex-1 overflow-auto space-y-3 custom-scrollbar">
                {getItemsForDay(selectedDay).map((item, i) => (
                  <motion.div 
                    key={i}
                    onMouseEnter={() => setHoveredProjectId(item.projectId)}
                    onMouseLeave={() => setHoveredProjectId(null)}
                    onClick={() => setEditingItem({ item, projectId: item.projectId, type: 'task' })}
                    className="bg-base-900 p-4 rounded-xl border border-base-700 hover:border-primary cursor-pointer transition-all shadow-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-xs text-white">{item.name}</span>
                      <div className="w-3 h-3 rounded-full shadow-inner border border-white/10" style={{ backgroundColor: getStatusColor(item.status) }}></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{item.projectName}</div>
                      <div className="w-10 h-1 rounded-full" style={{ backgroundColor: item.color }}></div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ItemEditorModal isOpen={!!editingItem} onClose={() => setEditingItem(null)} {...editingItem} />
    </motion.div>
  );
};